import { AxisTickProps } from '@nivo/axes';
import { BarDatum, ComputedDatum, ResponsiveBar } from '@nivo/bar';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { TwTheme } from '../../../Tw';
import { FormattedValueWithUnit } from '../../common/formatHelper';
import ChartTooltip from '../../simulation/panels/ChartTooltip';

// Add a fallback in case TwTheme.extend.colors is null or undefined
const colors = TwTheme?.extend?.colors || {
  menu: {
    DEFAULT: '#2a2a2a',
    disabled: '#4a4a4a',
  },
};

export type StackedBarProps<T extends BarDatum> = {
  data: T[];
  indexBy: string;
  dataKeys: string[];
  valueFormatter: (v: number) => FormattedValueWithUnit;
  getLabel: (data: ComputedDatum<T>) => string;
  getColor: (data: ComputedDatum<T>) => string;
  hideSmallLabelsThreshold?: number; // Percentage threshold below which labels are hidden
};

const CustomTick = (tick: AxisTickProps<string>) => {
  return (
    <></>
    // <g transform={`translate(${tick.x},${tick.y + 25})`}>
    //   {/* <rect
    //     x={-30}
    //     y={-12}
    //     rx={3}
    //     ry={3}
    //     width={60}
    //     height={24}
    //     fill={colors.menu.disabled}
    //   /> */}
    //   <text
    //     textAnchor="middle"
    //     dominantBaseline="middle"
    //     style={{
    //       // fill: getBestContrastColor(colors.menu.disabled),
    //       fontSize: 13,
    //     }}
    //   >
    //     {tick.value}
    //   </text>
    // </g>
  );
};

function StackedBar<T extends BarDatum>(props: StackedBarProps<T>) {
  const { t } = useTranslation('app');
  // Ensure we have valid data
  const safeData = props.data || [];
  const safeDataKeys = props.dataKeys || [];
  const safeIndexBy = props.indexBy || '';

  // Provide safe default functions if the required functions are not provided
  const safeGetLabel = (data: ComputedDatum<T>) => {
    try {
      return typeof props.getLabel === 'function'
        ? props.getLabel(data)
        : String(data.value || '');
    } catch (e) {
      console.error('Error in getLabel function:', e);
      return String(data?.value || '');
    }
  };

  const safeGetColor = (data: ComputedDatum<T>) => {
    try {
      return typeof props.getColor === 'function'
        ? props.getColor(data)
        : '#888888';
    } catch (e) {
      console.error('Error in getColor function:', e);
      return '#888888'; // Fallback gray color
    }
  };

  // Safe valueFormatter function
  const safeValueFormatter = (v: number): FormattedValueWithUnit => {
    try {
      return typeof props.valueFormatter === 'function'
        ? props.valueFormatter(v)
        : {
            value: String(v),
            fullString: String(v),
            unit: '',
            raw: v,
            unitCode: '',
          };
    } catch (e) {
      console.error('Error in valueFormatter function:', e);
      return {
        value: String(v),
        fullString: String(v),
        unit: '',
        raw: v,
        unitCode: '',
      };
    }
  };

  // Determine label configuration based on data characteristics
  const determineAxisConfig = () => {
    try {
      const dataCount = safeData.length;
      const hasLongLabels = safeData.some(item => {
        try {
          const label = String(item[safeIndexBy] || '');
          return label.length > 10;
        } catch (e) {
          return false;
        }
      });

      // Few items (< 5) with short labels: large text, no rotation
      if (dataCount < 5 && !hasLongLabels) {
        return {
          tickRotation: 0,
          fontSize: 12,
          tickPadding: 5,
        };
      }

      // Long labels: rotate 90 degrees
      if (hasLongLabels) {
        return {
          tickRotation: 90,
          fontSize: dataCount < 20 ? 12 : 8,
          tickPadding: 5,
        };
      }

      // Many items: rotate and use smaller font
      if (dataCount >= 20) {
        return {
          tickRotation: 90,
          fontSize: dataCount > 10 ? 7 : 9,
          tickPadding: 5,
        };
      }
    } catch (e) {
      console.error('Error determining axis config:', e);
    }

    // Default fallback
    return {
      tickRotation: 0,
      fontSize: 10,
      tickPadding: 5,
    };
  };

  const axisConfig = determineAxisConfig();

  // Find the maximum value in the dataset for relative comparison
  let maxValue = 0;
  try {
    safeData.forEach(item => {
      safeDataKeys.forEach(key => {
        try {
          const itemValue = Number(item[key]);
          if (!isNaN(itemValue) && itemValue > maxValue) {
            maxValue = itemValue;
          }
        } catch (e) {
          // Skip this item if there's an error
        }
      });
    });
  } catch (e) {
    console.error('Error calculating max value:', e);
  }

  // If we have no data, return an empty div to prevent errors
  if (safeData.length === 0 || safeDataKeys.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        {t`No data available`}
      </div>
    );
  }

  return (
    <ResponsiveBar
      data={safeData}
      indexBy={safeIndexBy as string}
      keys={safeDataKeys as string[]}
      valueFormat={v => {
        try {
          // Add a safeguard to check if valueFormatter is a function
          return safeValueFormatter(v).fullString;
        } catch (e) {
          console.error('Error formatting value:', e);
          return String(v);
        }
      }}
      colors={safeGetColor}
      label={node => {
        try {
          // Default threshold is 5% of max value if not specified
          const threshold = props.hideSmallLabelsThreshold ?? 5;

          // Only show label if value is above threshold percentage of max
          if (maxValue > 0 && (node.value / maxValue) * 100 >= threshold) {
            return safeGetLabel(node);
          }
        } catch (e) {
          console.error('Error in label function:', e);
        }
        return ''; // Return empty string to hide the label
      }}
      margin={{
        top: 10,
        right: 0,
        bottom: axisConfig.tickRotation === 90 ? 100 : 40,
        left: 0,
      }}
      axisTop={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: axisConfig.tickPadding,
        tickRotation: axisConfig.tickRotation,
        format: value => {
          _.truncate(value, {
            length: 20,
            separator: ' ',
          });
        },
      }}
      axisRight={null}
      axisLeft={null}
      enableGridY={true}
      padding={0.1}
      innerPadding={0}
      labelTextColor={{ from: 'color', modifiers: [['darker', 50]] }}
      theme={{
        grid: {
          line: {
            stroke: colors?.menu?.DEFAULT || '#2a2a2a',
            strokeWidth: 1,
          },
        },
        axis: {
          ticks: {
            text: {
              fontSize: axisConfig.fontSize,
              fill: 'rgba(255, 255, 255, 0.8)',
            },
          },
        },
      }}
      tooltip={datum => {
        if (!datum) return null;

        try {
          return (
            <>
              <ChartTooltip
                color={[datum?.color || '#888888', datum?.color || '#888888']}
                title={safeGetLabel(datum)}
                value={datum?.formattedValue || ''}
              />
            </>
          );
        } catch (e) {
          console.error('Error rendering tooltip:', e);
          return null;
        }
      }}
    />
  );
}

export default StackedBar;
